// The library's tag vocabulary: WHAT a clip is, beside WHERE it lives.
//
// One string shape: `group:value` from the closed groups below (Live's
// browser filter groups, docs/sdk/live-browser.md), or a bare free word.
// Lower-case, [a-z0-9-]. Type (loop / one-shot / MIDI) is never a stored
// tag — it is the row's own kind, and the shelf adds it to the row's tags
// itself. Key values are slugs (`fs-minor`, shown "F♯ minor") because `#` is
// the search box's tag syntax.
//
// Pure data, no runtime deps: the worker validates with it, the web app
// labels and completes with it, the extension labels the hub's rows with it.

export type TagGroupId = "drums" | "sounds" | "character" | "genre" | "key" | "tempo";

export interface TagValue {
  id: string;
  label: string;
  /** The words producers type and name files with — "hat", "hh", "vox",
   * "dnb", "bass drum". A `#word` in the search matches a tag by prefix on
   * its id, its label's words and these; the name reader tags a file that
   * carries one of them as a word; the completion scores them. */
  aliases?: readonly string[];
}

export interface TagGroup {
  id: TagGroupId;
  label: string;
  /** The closed list of values, or null when the value is free-form within
   * a rule (`tempo:` is an integer BPM). */
  values: ReadonlyArray<TagValue> | null;
}

const v = (id: string, label: string, aliases?: readonly string[]): TagValue => (aliases ? { id, label, aliases } : { id, label });

/** Root slugs, by pitch class from C. `#` is search syntax, so `cs` not `c#`. */
export const KEY_ROOTS: ReadonlyArray<{ id: string; label: string }> = [
  v("c", "C"), v("cs", "C♯"), v("d", "D"), v("ds", "D♯"), v("e", "E"), v("f", "F"),
  v("fs", "F♯"), v("g", "G"), v("gs", "G♯"), v("a", "A"), v("as", "A♯"), v("b", "B"),
];
export type KeyMode = "major" | "minor";

export const TAG_GROUPS: ReadonlyArray<TagGroup> = [
  {
    id: "drums",
    label: "Drums",
    values: [
      v("kick", "Kick", ["kicks", "bd", "bassdrum", "bass drum"]),
      v("snare", "Snare", ["snares", "sn", "sd", "snr"]),
      v("clap", "Clap", ["claps", "snap", "snaps"]),
      v("hihat", "Hi-hat", ["hat", "hats", "hh", "hihats", "hi hat", "hi hats", "open hat", "closed hat", "oh", "ch"]),
      v("cymbal", "Cymbal", ["cymbals", "ride", "crash", "splash", "china"]),
      v("tom", "Tom", ["toms", "floor tom"]),
      v("percussion", "Percussion", ["perc", "percs", "rim", "rimshot", "rims", "shaker", "shakers", "conga", "congas", "bongo", "bongos", "tamb", "tambourine", "cowbell", "woodblock"]),
      v("drum-loop", "Drum loop", ["break", "breaks", "breakbeat", "breakbeats", "drumloop", "drums loop", "beat", "beats", "groove", "grooves", "fill", "fills"]),
    ],
  },
  {
    id: "sounds",
    label: "Sounds",
    values: [
      v("bass", "Bass", ["sub", "subs", "808", "808s", "reese", "bassline", "low end"]),
      v("brass", "Brass", ["horn", "horns", "trumpet", "trombone", "tuba"]),
      v("guitar-plucked", "Guitar & plucked", ["pluck", "plucks", "gtr", "guitar", "guitars", "acoustic guitar", "banjo", "ukulele", "harp"]),
      v("lead", "Lead", ["leads", "solo", "melody", "melodies", "riff", "riffs", "hook", "hooks"]),
      v("mallets", "Mallets", ["mallet", "marimba", "vibes", "vibraphone", "xylophone", "glockenspiel", "glock", "kalimba", "bells"]),
      v("pad", "Pad", ["pads", "drone", "drones", "swell", "swells"]),
      v("piano-keys", "Piano & keys", ["piano", "pianos", "keys", "rhodes", "wurli", "wurlitzer", "organ", "epiano", "e piano", "clav", "clavinet", "synth keys"]),
      v("strings", "Strings", ["string", "violin", "violins", "viola", "cello", "cellos", "orchestra", "orchestral"]),
      v("voice", "Voice", ["vox", "vocal", "vocals", "voices", "choir", "adlib", "adlibs", "ad lib", "chant", "chants", "acapella"]),
      v("woodwind", "Woodwind", ["sax", "saxophone", "flute", "flutes", "clarinet", "oboe", "woodwinds"]),
      v("ambience-fx", "Ambience & FX", ["fx", "sfx", "riser", "risers", "impact", "impacts", "sweep", "sweeps", "noise", "texture", "textures", "ambience", "atmos", "atmosphere", "foley", "whoosh", "downlifter", "uplifter", "transition"]),
    ],
  },
  {
    id: "character",
    label: "Character",
    values: [
      v("acoustic", "Acoustic"), v("airy", "Airy"), v("bright", "Bright"), v("clean", "Clean"), v("dark", "Dark"), v("distorted", "Distorted"),
      v("dry", "Dry"), v("evolving", "Evolving"), v("glitchy", "Glitchy"), v("lo-fi", "Lo-fi"), v("mellow", "Mellow"), v("noisy", "Noisy"),
      v("punchy", "Punchy"), v("soft", "Soft"), v("warm", "Warm"), v("wet", "Wet"),
    ],
  },
  {
    id: "genre",
    label: "Genre",
    values: [
      v("ambient", "Ambient"),
      v("drum-and-bass", "Drum & Bass", ["dnb", "d n b", "drum n bass", "jungle", "liquid"]),
      v("hip-hop", "Hip Hop", ["hiphop", "boom bap", "boombap"]),
      v("house", "House", ["deep house", "tech house"]),
      v("lo-fi", "Lo-fi", ["lofi"]),
      v("pop", "Pop"),
      v("techno", "Techno"),
      v("trap", "Trap"),
      v("rnb", "R&B", ["r n b"]),
      v("rock", "Rock", ["indie"]),
      v("jazz", "Jazz", ["jazzy"]),
      v("experimental", "Experimental"),
    ],
  },
  {
    id: "key",
    label: "Key",
    // "F♯m" is typed `fsm` (normalizeTag turns ♯ and # into s), so the
    // aliases are the slug forms: fsm · fsmin · fsmaj.
    values: KEY_ROOTS.flatMap((r) => [
      v(`${r.id}-major`, `${r.label} major`, [`${r.id}maj`, `${r.id} major`, `${r.id} maj`]),
      v(`${r.id}-minor`, `${r.label} minor`, [`${r.id}m`, `${r.id}min`, `${r.id} minor`, `${r.id} min`]),
    ]),
  },
  { id: "tempo", label: "Tempo", values: null },
];

/** The order groups take in a chip row: what a producer reaches for first. */
export const GROUP_ORDER: ReadonlyArray<TagGroupId> = ["drums", "sounds", "key", "genre", "character", "tempo"];

export const LIBRARY_TAG_MAX_PER_ITEM = 32;
/** Suggestions an upload may attach to one clip. */
export const LIBRARY_TAG_SUGGESTIONS_MAX = 8;
export const LIBRARY_SMART_FOLDER_MAX = 50;
export const SMART_QUERY_MAX = 200;
export const TAG_MAX_LENGTH = 32;

const PART = "[a-z0-9][a-z0-9-]{0,31}";
const TAG_RE = new RegExp(`^${PART}(?::${PART})?$`);

export function tagGroup(id: string): TagGroup | null {
  return TAG_GROUPS.find((g) => g.id === id) ?? null;
}

export function parseTag(tag: string): { group: TagGroupId | null; value: string } {
  const at = tag.indexOf(":");
  if (at < 0) return { group: null, value: tag };
  const g = tagGroup(tag.slice(0, at));
  return g ? { group: g.id, value: tag.slice(at + 1) } : { group: null, value: tag };
}

/** A group value, when the tag names one. */
export function tagValue(tag: string): TagValue | null {
  const { group, value } = parseTag(tag);
  if (group === null) return null;
  return tagGroup(group)?.values?.find((x) => x.id === value) ?? null;
}

const WORDS_CACHE = new Map<string, readonly string[]>();

/**
 * The words a tag answers to by prefix: the tag itself (`drums:hihat`), its
 * value (`hihat`), each word of its label (`hi`, `hat`), and its aliases in
 * both their spaced and dashed forms (`hi hat`, `hi-hat`). A free tag
 * answers to itself alone.
 */
export function tagWords(tag: string): readonly string[] {
  const hit = WORDS_CACHE.get(tag);
  if (hit) return hit;
  const val = tagValue(tag);
  const out = new Set<string>([tag.toLowerCase()]);
  if (val) {
    out.add(val.id);
    for (const w of val.label.toLowerCase().split(/[^a-z0-9#♯]+/)) if (w) out.add(w.replace(/[#♯]/g, "s"));
    for (const a of val.aliases ?? []) {
      out.add(a);
      out.add(a.replace(/\s+/g, "-"));
    }
  }
  const words = [...out];
  WORDS_CACHE.set(tag, words);
  return words;
}

/** Whether a `#word` of the search reaches this tag: a prefix of any of its words. */
export function tagAnswersTo(tag: string, word: string): boolean {
  if (word === "") return true;
  return tagWords(tag).some((w) => w.startsWith(word));
}

/** Whether a string is a tag this library stores: a free word, or a group value the group knows. */
export function validTag(tag: unknown): tag is string {
  if (typeof tag !== "string" || !TAG_RE.test(tag)) return false;
  const at = tag.indexOf(":");
  if (at < 0) return true;
  const g = tagGroup(tag.slice(0, at));
  if (!g) return false;
  const value = tag.slice(at + 1);
  if (g.values === null) {
    const n = Number(value);
    return /^\d+$/.test(value) && n >= 20 && n <= 999;
  }
  return g.values.some((x) => x.id === value);
}

/** What a typed word becomes: trimmed, lower-case, spaces and odd marks to dashes. */
export function normalizeTag(input: string): string {
  return input
    .trim()
    .toLowerCase()
    .replace(/[#♯]/g, "s")
    .replace(/[^a-z0-9:-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, TAG_MAX_LENGTH * 2 + 1);
}

/** "Kick" / "F♯ minor" / "128 BPM" / "#glassy". */
export function tagLabel(tag: string): string {
  const { group, value } = parseTag(tag);
  if (group === null) return `#${value}`;
  const g = tagGroup(group)!;
  if (g.values === null) return group === "tempo" ? `${value} BPM` : value;
  return g.values.find((x) => x.id === value)?.label ?? value;
}

/** The group's own name, for a chip's muted prefix. */
export function groupLabel(tag: string): string | null {
  const { group } = parseTag(tag);
  return group ? (tagGroup(group)?.label ?? null) : null;
}

/** `key:fs-minor` for pitch class 6, minor. */
export function keyTag(rootPc: number, mode: KeyMode): string {
  const root = KEY_ROOTS[((rootPc % 12) + 12) % 12]!;
  return `key:${root.id}-${mode}`;
}

/** Group tags first in GROUP_ORDER, then free tags; stable within a group. */
export function sortTags(tags: readonly string[]): string[] {
  const rank = (t: string): number => {
    const { group } = parseTag(t);
    return group === null ? GROUP_ORDER.length : GROUP_ORDER.indexOf(group);
  };
  return [...tags].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

/**
 * Whether a row's tags answer a set of chosen tags, Live's way: values of
 * ONE group OR together (Kick or Snare), groups AND, free words AND. Shared
 * by the shelf's `matches()` and the hub's `rowMatches()`.
 */
export function tagsSatisfy(rowTags: readonly string[], chosen: readonly string[]): boolean {
  const byGroup = new Map<string, string[]>();
  for (const t of chosen) {
    const { group } = parseTag(t);
    const k = group ?? `word:${t}`;
    byGroup.set(k, [...(byGroup.get(k) ?? []), t]);
  }
  for (const values of byGroup.values()) if (!values.some((t) => rowTags.includes(t))) return false;
  return true;
}

/** One group in a library's tag index: its values and how many rows carry each. */
export interface TagIndexGroup {
  /** A group id, or `words` for the free tags. */
  id: TagGroupId | "words";
  label: string;
  /** Rows carrying any tag of the group. */
  count: number;
  values: Array<{ tag: string; label: string; count: number }>;
}

/**
 * The tags a library holds, by group, with counts — what a "browse by tag"
 * lists. `rows` are each row's stored, confirmed tags (kind and family words
 * excluded by the caller). Groups in GROUP_ORDER, values by count then name.
 */
export function tagIndexOf(rows: ReadonlyArray<readonly string[]>): TagIndexGroup[] {
  const groups = new Map<string, { rows: Set<number>; values: Map<string, number> }>();
  rows.forEach((tags, i) => {
    for (const t of new Set(tags)) {
      const { group } = parseTag(t);
      if (group === "tempo") continue;
      const k = group ?? "words";
      const g = groups.get(k) ?? { rows: new Set<number>(), values: new Map<string, number>() };
      g.rows.add(i);
      g.values.set(t, (g.values.get(t) ?? 0) + 1);
      groups.set(k, g);
    }
  });
  const order: string[] = [...GROUP_ORDER.filter((g) => g !== "tempo"), "words"];
  return order
    .filter((id) => groups.has(id))
    .map((id) => {
      const g = groups.get(id)!;
      return {
        id: id as TagIndexGroup["id"],
        label: id === "words" ? "Your words" : tagGroup(id)!.label,
        count: g.rows.size,
        values: [...g.values.entries()]
          .map(([tag, count]) => ({ tag, label: tagLabel(tag), count }))
          .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label)),
      };
    });
}

// ---- Family words from a name ----------------------------------------------------
//
// Live's Browser auto-tags user samples by sound; before any analysis, a
// name is the next best signal — producers name things "Kick", "Bass",
// "Keys 2". These are the coarse FAMILY words the shelf's `#` search matches
// (drums, bass, keys, vocals, instruments, fx); the finer `drums:kick` tags
// are suggested by web-shared/src/lib/tags/ and stored.

const FAMILY_WORDS: ReadonlyArray<[RegExp, string]> = [
  [/\b(kick|bd|snare|sn|clap|hat|hh|hihat|hi-hat|cymbal|ride|crash|tom|perc|percussion|drum|drums|beat|break|loop)\b/i, "drums"],
  [/\b(bass|sub|808)\b/i, "bass"],
  [/\b(key|keys|piano|rhodes|organ|epiano|e-piano|synth|pad|lead|pluck|arp|chord|chords|stab)\b/i, "keys"],
  [/\b(vox|vocal|vocals|voice|choir|adlib|ad-lib)\b/i, "vocals"],
  [/\b(gtr|guitar|acoustic|strings|violin|cello|brass|horn|horns|sax|flute|wind)\b/i, "instruments"],
  [/\b(fx|sfx|riser|impact|sweep|noise|texture|ambience|ambient|atmos|foley)\b/i, "fx"],
];

export function tagsForName(name: string): string[] {
  // "Sango_Hat_Slushy", "kick-01.wav": underscores and dashes are word
  // characters to a regex, so split on them first.
  const words = name.replace(/[_\-.]+/g, " ");
  const out: string[] = [];
  for (const [re, tag] of FAMILY_WORDS) if (re.test(words) && !out.includes(tag)) out.push(tag);
  return out;
}
